/**
 * ⛔⛔ THE DIAGNOSTICS RING BUFFER — Card P244.
 *
 * Staff are in San Diego 8–18 Nov. The point of this module is that when
 * somebody reports a problem, what we receive is the state of their screen and
 * the thirty seconds before it, rather than a paragraph written afterwards
 * from memory.
 *
 * ── THE RULE THAT SHAPES EVERY LINE BELOW ────────────────────────────────
 *
 * ⛔ NOTHING LEAVES THE BROWSER UNTIL THE USER PRESSES SUBMIT. This module
 * contains NO network call of any kind — no fetch, no invoke, no beacon, not
 * even a conditional one. It records into fixed-size arrays in memory and
 * hands a COPY to whoever asks. The dialog is what transmits, and it only
 * exists while the user is looking at it.
 *
 * That is not a style preference. A diagnostics collector that can transmit on
 * its own is an exfiltration path that runs on every page for every logged-in
 * clinical user, and no amount of "we only send on error" is checkable once
 * the capability is in the file. tests/unit/security/feedbackNoAutoSend.test.js
 * asserts the absence by reading this source.
 *
 * ── WHAT IS RECORDED, AND THE THREE THINGS THAT ARE NOT ──────────────────
 *
 *   breadcrumbs  route changes + the LABEL of a clicked control
 *   apiCalls     the IPC channel name, ok/status, duration
 *   errors       window errors + unhandled rejections (message + stack)
 *
 *   ⛔ NEVER a typed field value. Not from an input, textarea, select,
 *      contenteditable or a paste. See labelFor() — it reads only a
 *      control's accessible name, and REFUSES outright on a field element.
 *   ⛔ NEVER an IPC argument, request body, response body or query value.
 *      Only the channel name, which carries no ids. See wrapInvoke().
 *   ⛔ NEVER an unredacted string. Everything stored passes redactText /
 *      redactLabel first, so PHI is scrubbed BEFORE it is written down
 *      rather than before it is exported.
 */

import { redactText, redactLabel } from './feedbackRedact';

export const MAX_BREADCRUMBS = 20;
export const MAX_API_CALLS = 20;
export const MAX_ERRORS = 20;

/*
 * ⚠ "the last few minutes" (card item 2) made concrete. An error from an hour
 * ago is not context for this report, and keeping it only widens what a
 * submission carries. Breadcrumbs and API calls are bounded by COUNT because
 * twenty of them is a story; errors are bounded by count AND age because one
 * stuck error can otherwise sit in the buffer all day.
 */
export const ERROR_MAX_AGE_MS = 5 * 60 * 1000;

const state = {
  breadcrumbs: [],
  apiCalls: [],
  errors: [],
  installed: false,
  uninstall: [],
};

function push(arr, item, max) {
  arr.push(item);
  while (arr.length > max) arr.shift();
}

function now() {
  return new Date().toISOString();
}

/* ── breadcrumbs ──────────────────────────────────────────────────────── */

/*
 * ⛔⛔ THE FIELD REFUSAL IS THE CONTROL, NOT THE REDACTOR.
 *
 * It would be easy to read textContent off whatever was clicked and trust
 * redactText() to clean it. That is backwards. An <input> clicked by the user
 * has its value in `.value`, which textContent never returns — so a naive
 * implementation looks safe in a test that types and clicks. But a
 * contenteditable div DOES put typed text in textContent, and a <select>'s
 * textContent is every option including the chosen patient's name.
 *
 * So field elements are refused BY TAG before any text is read, and what is
 * read from everything else is the ACCESSIBLE NAME (aria-label, then the
 * button's own text), never the subtree of an arbitrary container.
 */
const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);

export function isFieldElement(el) {
  if (!el || !el.tagName) return false;
  if (FIELD_TAGS.has(el.tagName)) return true;
  // contenteditable holds typed text in textContent — the case a tag check alone misses.
  const ce = el.getAttribute && el.getAttribute('contenteditable');
  return ce === '' || ce === 'true';
}

export function labelFor(el) {
  if (!el || !el.tagName) return null;
  if (isFieldElement(el)) return null;

  const aria = el.getAttribute && el.getAttribute('aria-label');
  if (aria) return redactLabel(aria);

  const testid = el.getAttribute && el.getAttribute('data-testid');
  if (testid) return redactLabel(testid);

  /*
   * ⚠ textContent LAST, and only from the control itself. If the control
   * contains a field (a label wrapping an input), the field's typed value is
   * NOT in textContent — but a contenteditable child would be, so those are
   * stripped before reading.
   */
  const text = el.textContent;
  if (!text) return null;
  return redactLabel(text);
}

/** The nearest thing that is plausibly "a control the user clicked". */
function controlFor(target) {
  let el = target;
  let hops = 0;
  while (el && hops < 5) {
    if (!el.tagName) return null;
    if (isFieldElement(el)) return null; // clicked into a field — record nothing
    const tag = el.tagName;
    const role = el.getAttribute && el.getAttribute('role');
    if (tag === 'BUTTON' || tag === 'A' || role === 'button' || role === 'menuitem' || role === 'tab') {
      return el;
    }
    el = el.parentElement;
    hops += 1;
  }
  return null;
}

export function recordBreadcrumb(kind, label) {
  const l = redactLabel(label);
  if (!l) return;
  push(state.breadcrumbs, { t: now(), kind: String(kind), label: l }, MAX_BREADCRUMBS);
}

export function recordRoute(route) {
  push(state.breadcrumbs, { t: now(), kind: 'route', label: redactLabel(route) }, MAX_BREADCRUMBS);
}

/* ── API calls ────────────────────────────────────────────────────────── */

/**
 * ⛔⛔ THE CHANNEL NAME IS THE WHOLE RECORD.
 *
 * Every call in this app goes through POST /api/ipc { channel, args }
 * (public/apiClient.js). There is no URL variation to template and no path
 * ids to replace: the identity of a call IS its channel name
 * ("crm:opportunities:list"), and channel names are a fixed vocabulary that
 * contains no ids and no user values.
 *
 * The ids and values live in `args` — which this function receives and
 * DELIBERATELY NEVER READS. Not its length, not its keys. The card asked for
 * ":id templating"; the transport makes that unnecessary, because there is
 * nothing to template. An API log that structurally cannot hold a value beats
 * one scrubbed into safety by a regex that might miss a shape.
 */
export function recordApiCall(channel, { ok, status, ms }) {
  push(state.apiCalls, {
    t: now(),
    channel: redactLabel(channel),
    ok: !!ok,
    /*
     * ⚠ A STRING, NOT A NUMBER. The bridge's failure vocabulary is CODES
     * ("NO_HANDLER", "FORBIDDEN"), not HTTP numerics — POST /api/ipc answers
     * 200 even for an unknown channel, deliberately, because CloudFront
     * rewrites a 404 into index.html. Coercing with Number() turned every
     * failed call's status into NaN: the API log was at its least informative
     * on exactly the calls a bug report is about. Caught by the red arm in
     * tests/react/feedbackDiagnostics.test.js.
     */
    status: status == null ? null : String(status).slice(0, 40),
    ms: ms == null ? null : Math.round(Number(ms)),
  }, MAX_API_CALLS);
}

export function recordError(message, stack) {
  push(state.errors, {
    t: now(),
    message: redactText(String(message || '')).slice(0, 500),
    stack: redactText(String(stack || '')).slice(0, 2000),
  }, MAX_ERRORS);
}

/* ── install ──────────────────────────────────────────────────────────── */

function wrapInvoke(win) {
  const api = win.zignagenixAPI;
  if (!api || typeof api.invoke !== 'function' || api.__zgDiagWrapped) return null;

  const original = api.invoke;
  /*
   * ⚠ ...args IS ACCEPTED AND NEVER INSPECTED. It is forwarded straight
   * through. Spreading it is not reading it: nothing below touches args[0].
   */
  const wrapped = function zgDiagInvoke(channel, ...args) {
    const started = (win.performance && win.performance.now) ? win.performance.now() : Date.now();
    const finish = (ok, status) => {
      const ended = (win.performance && win.performance.now) ? win.performance.now() : Date.now();
      recordApiCall(channel, { ok, status, ms: ended - started });
    };
    let out;
    try {
      out = original.call(api, channel, ...args);
    } catch (e) {
      finish(false, null);
      throw e;
    }
    if (out && typeof out.then === 'function') {
      return out.then(
        (res) => {
          /*
           * ⚠ THE BRIDGE REPORTS APPLICATION ERRORS AS A RESOLVED VALUE
           * ({ error: true, code }), not a rejection — see public/apiClient.js.
           * Treating "resolved" as "ok" would make the API log say every call
           * succeeded on exactly the screens a bug report is filed about.
           *
           * ⛔ `code` is a fixed vocabulary (NO_HANDLER, FORBIDDEN…) and is the
           * only field read off the response. The response BODY is not touched.
           */
          const failed = !!(res && res.error === true);
          finish(!failed, failed ? (res.code || 'error') : 'ok');
          return res;
        },
        (err) => { finish(false, (err && err.code) || 'throw'); throw err; },
      );
    }
    finish(true, 'ok');
    return out;
  };

  wrapped.__zgDiagWrapped = true;
  api.invoke = wrapped;
  api.__zgDiagWrapped = true;
  return () => { api.invoke = original; delete api.__zgDiagWrapped; };
}

/**
 * Install the listeners. Idempotent; returns an uninstall function.
 *
 * ⛔⛔ addEventListener('error'), NEVER window.onerror = fn.
 *
 * src/index.js:32 ASSIGNS window.onerror to a live debug interceptor for the
 * invariant-31 bug. Assigning it again here would silently delete that
 * interceptor — no error, no warning, and the thing it was installed to catch
 * would simply stop being caught. addEventListener coexists with the
 * assignment; both run. tests/react/feedbackDiagnostics.test.js has an arm.
 */
export function install(win = (typeof window !== 'undefined' ? window : null)) {
  if (!win || state.installed) return () => {};
  state.installed = true;

  const onClick = (ev) => {
    try {
      const el = controlFor(ev.target);
      if (!el) return;
      const label = labelFor(el);
      if (label) recordBreadcrumb('click', label);
    } catch (e) { /* diagnostics must never break the app */ }
  };
  win.addEventListener('click', onClick, true);
  state.uninstall.push(() => win.removeEventListener('click', onClick, true));

  const onError = (ev) => {
    try {
      recordError(ev && (ev.message || (ev.error && ev.error.message)), ev && ev.error && ev.error.stack);
    } catch (e) { /* ignore */ }
  };
  win.addEventListener('error', onError);
  state.uninstall.push(() => win.removeEventListener('error', onError));

  const onRejection = (ev) => {
    try {
      const r = ev && ev.reason;
      recordError(r && (r.message || String(r)), r && r.stack);
    } catch (e) { /* ignore */ }
  };
  win.addEventListener('unhandledrejection', onRejection);
  state.uninstall.push(() => win.removeEventListener('unhandledrejection', onRejection));

  const unwrap = wrapInvoke(win);
  if (unwrap) state.uninstall.push(unwrap);

  return () => {
    state.uninstall.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
    state.uninstall = [];
    state.installed = false;
  };
}

/* ── read ─────────────────────────────────────────────────────────────── */

/**
 * A COPY of what has been recorded. Callers cannot mutate the buffer, and
 * calling this does not transmit anything — the dialog decides that.
 */
export function snapshot(nowMs = Date.now()) {
  const fresh = state.errors.filter((e) => {
    const age = nowMs - Date.parse(e.t);
    return Number.isNaN(age) || age <= ERROR_MAX_AGE_MS;
  });
  return {
    breadcrumbs: state.breadcrumbs.map((b) => ({ ...b })),
    apiCalls: state.apiCalls.map((a) => ({ ...a })),
    errors: fresh.map((e) => ({ ...e })),
  };
}

/** Test seam. */
export function _reset() {
  state.breadcrumbs = [];
  state.apiCalls = [];
  state.errors = [];
}
